import { useIntl } from '@/i18n';
import AdminAccountDetailDrawer from '@/pages/muou/sys/admin-account/components/detail/AdminAccountDetailDrawer';
import useAdminAccountDetail from '@/pages/muou/sys/admin-account/hooks/useAdminAccountDetail';
import { getIamSession } from '@/services/iam-admin';
import { useAuthStore } from '@/store/auth';
import type { IamSessionResp } from '@/types/iam-admin';
import { feedback } from '@/utils/feedback';
import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import CurrentLoginModal from './CurrentLoginModal';

/** 当前登录信息按打开取正式会话；帐号详情复用已有业务抽屉。 */
export default function UserDropdown() {
  const intl = useIntl();
  const navigate = useNavigate();
  const accountName = useAuthStore((state) => state.accountName);
  const t = (id: string, values?: Record<string, unknown>) =>
    intl.formatMessage({ id }, values);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<IamSessionResp>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const accountDetail = useAdminAccountDetail();

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setSession(undefined);
    setFailed(false);
    getIamSession({ signal: controller.signal }).then(
      (value) => {
        if (!controller.signal.aborted) setSession(value);
      },
      () => {
        if (!controller.signal.aborted) setFailed(true);
      },
    );
    return () => controller.abort();
  }, [open, attempt]);

  const close = () => {
    setOpen(false);
    setSession(undefined);
    setFailed(false);
  };
  const logout = () =>
    feedback.modal.confirm({
      className: 'admin-modal',
      centered: true,
      width: 520,
      title: t('layout.logoutConfirm'),
      content: t('layout.logoutConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        await useAuthStore.getState().signOut();
        navigate('/login', { replace: true });
      },
    });
  const name = accountName ?? '—';

  return (
    <>
      <button
        type="button"
        className="admin-header-account hover:bg-surface-soft focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-primary focus-visible:outline-offset-2 flex items-center gap-2.5 py-1.5 px-2 border-0 rounded-md bg-transparent text-body cursor-pointer [font-family:inherit] text-[13px] font-medium leading-[20px] whitespace-nowrap"
        onClick={() => setOpen(true)}
      >
        <Avatar size={38} className="admin-header-avatar" aria-hidden>
          {accountName?.[0] ?? <UserOutlined />}
        </Avatar>
        <span className="truncate text-[13px]">{name}</span>
        <DownOutlined aria-hidden />
      </button>
      <CurrentLoginModal
        open={open}
        name={name}
        session={session}
        failed={failed}
        detailLoading={accountDetail.loading}
        onClose={close}
        onDetail={() => {
          if (!session) return;
          accountDetail.show(session.accountId);
          close();
        }}
        onPassword={() => {
          close();
          navigate('/password/change');
        }}
        onLogout={logout}
        onRetry={() => setAttempt((value) => value + 1)}
      />
      <AdminAccountDetailDrawer
        open={accountDetail.open}
        detail={accountDetail.detail}
        loading={accountDetail.loading}
        onClose={accountDetail.close}
      />
    </>
  );
}
